package org.zest.insan_360_website.data.page_states

import org.zest.insan_360_website.data.localized

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.browser.storage.StringStorageKey
import com.varabyte.kobweb.browser.storage.getItem
import com.varabyte.kobweb.browser.storage.removeItem
import com.varabyte.kobweb.browser.storage.setItem
import com.varabyte.kobweb.core.PageContext
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import org.zest.insan_360_website.components.widgets.ModalState
import org.zest.insan_360_website.components.widgets.ToastState
import org.zest.insan_360_website.components.widgets.ToastType
import org.zest.insan_360_website.data.LanguageSettings
import org.zest.insan_360_website.data.page_states.SignUpPageState.currentRegState
import org.zest.insan_360_website.data.page_states.SignUpPageState.signUpState
import org.zest.insan_360_website.model.LoginPayload
import org.zest.insan_360_website.model.LoginRequest
import org.zest.insan_360_website.model.SignupRequest
import org.zest.insan_360_website.model.UserRole
import org.zest.insan_360_website.pages.RegisterState
import org.zest.insan_360_website.pages.SignUpState
import org.zest.insan_360_website.utils.NetworkUtils
import org.zest.insan_360_website.utils.processResponse
import kotlin.coroutines.cancellation.CancellationException

val USER_TOKEN_KEY = StringStorageKey("jwt_token", null)

object AuthState {
    private var _token by mutableStateOf<String?>(null)
    private var cachedClaims by mutableStateOf<Map<String, String>>(emptyMap())

    val token: String? get() = _token

    var isLoggedIn by mutableStateOf(false)
        private set

    val isTokenExpired: Boolean
        get() {
            val expString = cachedClaims["exp"] ?: return true
            val expSeconds = expString.toLongOrNull() ?: return true
            val currentTimeSeconds = kotlin.js.Date.now() / 1000
            return currentTimeSeconds >= (expSeconds - 10)
        }

    val role: UserRole
        get() = cachedClaims["role"]?.let {
            runCatching { UserRole.valueOf(it) }.getOrNull()
        } ?: UserRole.STUDENT

    val email: String? get() = cachedClaims["sub"]
    val name: String? get() = cachedClaims["name"]
    val orgId: String? get() = cachedClaims["organization"]

    private fun setToken(newToken: String?) {
        _token = newToken
        cachedClaims = newToken?.let { parseClaims(it) } ?: emptyMap()
    }

    private fun parseClaims(jwt: String): Map<String, String> {
        return try {
            val segments = jwt.split(".")
            if (segments.size < 2) return emptyMap()
            val decodedJson = window.atob(segments[1])
            val jsonObject = Json.parseToJsonElement(decodedJson).jsonObject
            jsonObject.entries.mapNotNull { (key, value) ->
                try { key to value.jsonPrimitive.content } catch (_: Throwable) { null }
            }.toMap()
        } catch (_: Throwable) {
            emptyMap()
        }
    }

    fun requestLogin(
        email: String,
        password: String,
        toast: ToastState,
        ctx: PageContext,
        scope: CoroutineScope,
        onCompleted: () -> Unit = { }
    ) {
        scope.launch {
            try {
                val request = LoginRequest(email, password)
                val response = NetworkUtils.postBasicApi("/api/auth/login", request)

                val loginData = processResponse<LoginPayload>(
                    response = response,
                    toast = toast,
                    fallbackErrorLog = "Login Failed"
                )

                if (loginData != null) {
                    setToken(loginData.token)
                    localStorage.setItem(USER_TOKEN_KEY, loginData.token)
                    isLoggedIn = true
                    ctx.router.navigateTo("/user")
                }

            } catch (e: Throwable) {
                if (e is CancellationException) throw e
                toast.show(
                    LanguageSettings.General.networkFailure.localized,
                    ToastType.ERROR
                )
                console.error(e)
            } finally {
                onCompleted()
            }
        }
    }

    fun requestSignup(
        scope: CoroutineScope,
        toast: ToastState,
        onCompleted: () -> Unit
    ) {
        scope.launch {
            try {
                signUpState.apply {
                    val cleanName = name.trim()
                    val cleanEmail = email.trim()
                    val cleanNationalId = nationalId.trim()
                    val cleanPhone = phone.trim()
                    val cleanWorkType = workType.trim()
                    val cleanOrganization = organization.trim()
                    val cleanPosition = position.trim()
                    val postalAndCity = "${postalCode.trim()} ${city.trim()}".trim()
                    val fullAddress = listOf(addressLine1.trim(), addressLine2.trim(), postalAndCity, state.trim(), country.trim())
                        .filter { it.isNotBlank() }
                        .joinToString(", ")

                    val request: SignupRequest = if (organization.isNotBlank() && position.isNotBlank()) {
                        SignupRequest(
                            cleanName, cleanEmail, password, cleanNationalId,
                            fullAddress, cleanPhone, cleanWorkType, cleanPosition, cleanOrganization
                        )
                    } else {
                        SignupRequest(
                            cleanName, cleanEmail, password, cleanNationalId,
                            fullAddress, cleanPhone, cleanWorkType
                        )
                    }

                    val headers = Headers().apply {
                        append("Content-Type", "application/json")
                    }
                    val requestInit = RequestInit(
                        method = "POST",
                        headers = headers,
                        body = Json.encodeToString(request)
                    )

                    val response = window.fetch("/api/auth/signup", requestInit).await()

                    val isSuccess = processResponse<Unit>(
                        response = response,
                        toast = toast,
                        fallbackErrorLog = "Signup Failed"
                    )

                    if (isSuccess != null) {
                        currentRegState = RegisterState.Default
                        signUpState = SignUpState()
                    }
                }
            } catch (e: Throwable) {
                if (e is CancellationException) throw e
                console.log(e)
                toast.show(
                    LanguageSettings.General.networkFailure.localized,
                    ToastType.ERROR
                )
            } finally {
                onCompleted()
            }
        }
    }

    suspend fun load() {
        val storedToken = localStorage.getItem(USER_TOKEN_KEY)

        if (!storedToken.isNullOrBlank() && storedToken.contains(".")) {
            setToken(storedToken)
            if (isTokenExpired) {
                silentRefresh()
                isLoggedIn = (token != null)
            } else {
                isLoggedIn = true
            }
        } else {
            setToken(null)
            isLoggedIn = false
        }
    }

    fun logout(modal: ModalState? = null, toast: ToastState? = null) {
        if (!isLoggedIn) return
        
        val tokenToRevoke = token
        
        if (modal != null) {
            window.history.replaceState(null, "", window.location.href)
            modal.hide(fromBackButton = true)
        }
        localStorage.removeItem(USER_TOKEN_KEY)
        setToken(null)
        clearAllStates()
        isLoggedIn = false

        val headers = Headers()
        if (!tokenToRevoke.isNullOrBlank()) {
            headers.append("Authorization", "Bearer $tokenToRevoke")
        }
        val requestInit = RequestInit(
            method = "POST",
            headers = headers
        )
        requestInit.asDynamic().keepalive = true
        
        window.fetch("/api/auth/logout", requestInit).catch { e ->
            console.warn("Logout API call failed.", e)
            null
        }
    }

    private var refreshDeferred: CompletableDeferred<Boolean>? = null

    suspend fun silentRefresh(): Boolean {

        refreshDeferred?.let { return it.await() }
        val deferred = CompletableDeferred<Boolean>()
        refreshDeferred = deferred

        return try {
            val requestInit = RequestInit(method = "POST")
            val response = window.fetch("/api/auth/refresh", requestInit).await()

            if (response.ok) {
                val responseText = response.text().await()
                val freshToken = Json.decodeFromString<String>(responseText)

                setToken(freshToken)
                localStorage.setItem(USER_TOKEN_KEY, freshToken)
                isLoggedIn = true
                deferred.complete(true)
                true
            } else {
                logout()
                deferred.complete(false)
                false
            }
        } catch (e: Throwable) {
            if (e is CancellationException) throw e
            console.warn("Network offline or fetch failed during silent refresh. Preserving session state. Error: {e.message}")
            deferred.complete(false)
            false
        } finally {
            refreshDeferred = null
        }
    }
}